/**
 * Site Manager Cockpit — API client.
 *
 * Calls the CIS Function API directly with an Entra access token as
 * `Authorization: Bearer` (getToken injected so tests and fixture mode do
 * not need MSAL). Endpoints per SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.2,
 * §4.6, §4.7 and azure-functions/cis/CisCockpitToday|Reports|Remind.
 *
 * Every exported method resolves — it NEVER rejects and NEVER hangs. This
 * is the fix for the 29 Sep live defect: a hung/throwing token acquisition
 * (auth.js#getToken) or a hung/rejecting fetch left every screen's loading
 * effect awaiting a promise that never settled, so every screen showed
 * "Loading…" forever with zero requests ever reaching the server. Token
 * acquisition and the network request are each bounded by a timeout and
 * wrapped so a throw or a hang becomes a resolved `{ ok:false, status:0,
 * body:{ errorCode } }` envelope instead of an unhandled rejection —
 * errorCode is one of "sign_in_required" (token step threw or returned
 * nothing usable), "timeout" (token step or request exceeded its bound), or
 * "network_error" (request rejected, e.g. offline).
 */

"use strict";

const DEFAULT_TOKEN_TIMEOUT_MS = 15000;
const DEFAULT_REQUEST_TIMEOUT_MS = 60000;
const DEFAULT_COMMIT_TIMEOUT_MS = 45000;
// Transient-read policy (13 Oct live defect: a backend deploy answered 503 for
// a short window and the screen stayed on its error). Idempotent reads (GET)
// are retried on a gateway-style status or a network error: 3 attempts in all,
// waiting 2 s then 4 s between them (about 10 s with the request time).
// Writes are never auto-retried.
export const TRANSIENT_STATUSES = [502, 503, 504];
export const DEFAULT_RETRY_DELAYS_MS = [2000, 4000];

/** True when a resolved request envelope is worth one more attempt. */
export function isTransientFailure(result) {
  if (!result || result.ok) return false;
  if (result.status === 0) return Boolean(result.body && result.body.errorCode === "network_error");
  return TRANSIENT_STATUSES.includes(result.status);
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs `attempt()` (resolves an envelope, never rejects) and repeats it
 * while the outcome is transient, waiting `delaysMs[i]` before retry i+1.
 * Returns the last envelope. Exported for tests.
 */
export async function withTransientRetry(attempt, { delaysMs = DEFAULT_RETRY_DELAYS_MS, sleep = defaultSleep } = {}) {
  let result = await attempt();
  for (let i = 0; i < delaysMs.length && isTransientFailure(result); i += 1) {
    await sleep(delaysMs[i]);
    result = await attempt();
  }
  return result;
}

/**
 * Runs `fn()` and resolves with `{ value }`, `{ error }` (fn threw or
 * rejected) or `{ timedOut: true }` (neither happened within `ms`) —
 * never rejects, regardless of what `fn` does. `fn` is a thunk (not a bare
 * promise) so a SYNCHRONOUS throw inside it is caught the same as an
 * asynchronous rejection.
 */
function withTimeout(fn, ms) {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve({ timedOut: true });
      }
    }, ms);
    Promise.resolve()
      .then(fn)
      .then(
        (value) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            resolve({ value });
          }
        },
        (error) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            resolve({ error });
          }
        }
      );
  });
}

/**
 * @param {object} opts
 * @param {string} opts.apiBase
 * @param {() => Promise<string|null>} opts.getToken
 * @param {typeof fetch} [opts.fetchImpl]
 * @param {number} [opts.tokenTimeoutMs]
 * @param {number} [opts.requestTimeoutMs]
 * @param {number} [opts.commitTimeoutMs] per-call bound for one import commit pass (default 45 s)
 * @param {number[]} [opts.retryDelaysMs] waits before each retry of a transient GET failure (default [2000, 4000]; [] disables)
 * @param {(ms:number)=>Promise<void>} [opts.sleep] injectable wait, for tests
 */
export function createApiClient({
  apiBase,
  getToken,
  fetchImpl = fetch,
  tokenTimeoutMs = DEFAULT_TOKEN_TIMEOUT_MS,
  requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  commitTimeoutMs = DEFAULT_COMMIT_TIMEOUT_MS,
  retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
  sleep = defaultSleep,
}) {
  /**
   * Resolves the auth headers for one call. A `getToken` that throws, or
   * that neither resolves nor rejects within `tokenTimeoutMs`, is reported
   * as an error code rather than propagated — a token that legitimately
   * resolves to `null` (no signed-in account / no usable cached token, the
   * existing offline behaviour) is NOT an error: the request still goes out
   * unauthenticated and the server's own 401 drives the error state, same
   * as before this fix.
   */
  async function resolveAuthHeaders(extra) {
    const headers = { ...(extra || {}) };
    if (!getToken) return { headers };
    const outcome = await withTimeout(() => getToken(), tokenTimeoutMs);
    if (outcome.timedOut) return { errorCode: "timeout" };
    if (outcome.error) return { errorCode: "sign_in_required" };
    if (outcome.value) headers.Authorization = `Bearer ${outcome.value}`;
    return { headers };
  }

  /**
   * Runs the network request with a real `AbortController` timeout (so a
   * live request is actually cancelled, not just abandoned) AND an outer
   * `withTimeout` race (so this resolves within `requestTimeoutMs` even if
   * `fetchImpl` ignores the abort signal entirely, as test doubles do).
   */
  async function fetchWithTimeout(path, init, timeoutMs = requestTimeoutMs) {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    const outcome = await withTimeout(
      () => fetchImpl(`${apiBase}${path}`, controller ? { ...init, signal: controller.signal } : init),
      timeoutMs
    );
    if (timer) clearTimeout(timer);
    if (outcome.timedOut) return { error: "timeout" };
    if (outcome.error) return { error: outcome.error && outcome.error.name === "AbortError" ? "timeout" : "network_error" };
    return { value: outcome.value };
  }

  /** Shared body for every endpoint below: resolve auth, fetch, parse — always resolves. */
  async function request(method, path, payload, timeoutMs) {
    // The one retry policy for the whole cockpit: GET only (idempotent).
    if (method !== "GET") return requestOnce(method, path, payload, timeoutMs);
    return withTransientRetry(() => requestOnce(method, path, payload, timeoutMs), { delaysMs: retryDelaysMs, sleep });
  }

  async function requestOnce(method, path, payload, timeoutMs) {
    const hasBody = payload !== undefined;
    const auth = await resolveAuthHeaders(hasBody ? { "Content-Type": "application/json" } : undefined);
    if (auth.errorCode) return { status: 0, ok: false, body: { errorCode: auth.errorCode } };
    const init = { method, headers: auth.headers };
    if (hasBody) init.body = JSON.stringify(payload);
    const fetched = await fetchWithTimeout(path, init, timeoutMs);
    if (fetched.error) return { status: 0, ok: false, body: { errorCode: fetched.error } };
    const res = fetched.value;
    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return { status: res.status, ok: res.ok, body };
  }

  const getJson = (path) => request("GET", path);
  const postJson = (path, payload) => request("POST", path, payload === undefined ? {} : payload);
  const patchJson = (path, payload) => request("PATCH", path, payload === undefined ? {} : payload);

  /** GET /api/cockpit/today — returns { generatedAtUtc, projects[], errors[], counts } or an error envelope. */
  async function fetchToday(date) {
    return getJson(date ? `/api/cockpit/today?date=${encodeURIComponent(date)}` : "/api/cockpit/today");
  }

  /**
   * POST /api/cockpit/records/review (day redesign phase 2, 2.3) — approve,
   * unapprove, undo or set a repair link; at most 50 items. Returns the RAW
   * per-item results array in the HTTP 200 envelope
   * (`[{ inspectionNumber, status: ok|stale_etag|not_found|invalid|record_voided, row?, etag? }]`).
   */
  async function reviewRecords(code, items) {
    return postJson("/api/cockpit/records/review", { code, items });
  }

  /** GET /api/projects/{code}/schedule?from=&to= — the stored schedule rows (RAW array), used to book a forecast item with its stored fields. */
  async function fetchScheduleRows(code, from, to) {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();
    return getJson(`/api/projects/${encodeURIComponent(code)}/schedule${qs ? `?${qs}` : ""}`);
  }

  /**
   * GET /api/cockpit/reports?weekStart= — returns { generatedAtUtc, weekStart, projects[], errors[] }.
   * With a custom `period` ({ periodStart, periodEnd }) the query carries the
   * period instead of the week (SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.2);
   * a refused period is a 422/409 body with its named `errorCode`.
   */
  async function fetchReports(weekStart, period) {
    const params = new URLSearchParams();
    if (period && period.periodStart && period.periodEnd) {
      params.set("periodStart", period.periodStart);
      params.set("periodEnd", period.periodEnd);
    } else if (weekStart) {
      params.set("weekStart", weekStart);
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    return getJson(`/api/cockpit/reports${qs}`);
  }

  /** POST /api/cockpit/remind { code, scheduleRef } — returns { status } or { errorCode }. */
  async function remind(code, scheduleRef) {
    return postJson("/api/cockpit/remind", { code, scheduleRef });
  }

  /** POST /api/cockpit/remind-photo { code, inspectionNumber } — SPEC-CIS-COCKPIT-READINESS-ACTIONS-001 §4.4. */
  async function remindPhoto(code, inspectionNumber, weekStart, period) {
    const body = { code, inspectionNumber };
    // The period on screen (section 4.2) replaces the week: a write must not
    // replace period readiness with week readiness.
    if (period && period.periodStart && period.periodEnd) {
      body.periodStart = period.periodStart;
      body.periodEnd = period.periodEnd;
    } else if (weekStart) {
      body.weekStart = weekStart;
    }
    return postJson("/api/cockpit/remind-photo", body);
  }

  /** POST /api/cockpit/photo-waiver { code, inspectionNumber, etag, reason } — §4.3, grant only (no revoke). */
  async function waivePhoto(code, inspectionNumber, etag, reason, weekStart, period) {
    const body = { code, inspectionNumber, etag, reason };
    if (period && period.periodStart && period.periodEnd) {
      body.periodStart = period.periodStart;
      body.periodEnd = period.periodEnd;
    } else if (weekStart) {
      body.weekStart = weekStart;
    }
    return postJson("/api/cockpit/photo-waiver", body);
  }

  /** GET one report's version list (the `versionsUrl` a history row carries); same-origin cockpit reports route only. */
  async function fetchReportVersions(versionsUrl) {
    if (typeof versionsUrl !== "string" || !versionsUrl.startsWith("/api/cockpit/reports?")) {
      return { ok: false, status: 0, body: { errorCode: "invalid_request" } };
    }
    return getJson(versionsUrl);
  }

  /** GET /api/cockpit/import-history?code=&page= — §4.5, read-only. */
  async function fetchImportHistory(code, page) {
    const params = new URLSearchParams({ code });
    if (page && page > 1) params.set("page", String(page));
    return getJson(`/api/cockpit/import-history?${params.toString()}`);
  }

  /**
   * GET /api/cockpit/plan?weekStart=&backlogOffset= — returns { generatedAtUtc,
   * weekStart, lanes[], items[], unbooked[], backlog[], backlogTotal,
   * backlogOffset, backlogLimit, imports[], errors[] }. `backlogOffset`
   * (CisCockpitPlan §4.3 backlog paging, PR #1958) pages the backlog list
   *20 rows at a time — omitted (or 0) reads the first page, same as before
   * this parameter existed.
   */
  async function fetchPlan(weekStart, backlogOffset) {
    const params = new URLSearchParams();
    if (weekStart) params.set("weekStart", weekStart);
    if (backlogOffset) params.set("backlogOffset", String(backlogOffset));
    const qs = params.toString();
    return getJson(`/api/cockpit/plan${qs ? `?${qs}` : ""}`);
  }

  /**
   * POST /api/projects/{code}/schedule — batch upsert, ≤50 items
   * (azure-functions/cis/CisScheduleImport, shared/validation.js
   * validateBatchShape). Returns `{ status, ok, body }` where `body` is the
   * RAW per-item results array (`[{ scheduleRef, status, readback?, errors? }]`)
   * — never wrapped, per the endpoint's own HTTP 200 batch envelope.
   */
  async function upsertSchedule(code, items) {
    return postJson(`/api/projects/${encodeURIComponent(code)}/schedule`, { items });
  }

  /**
   * POST /api/projects/{code}/schedule/imports — multipart preview step
   * (azure-functions/cis/CisScheduleImports, shared/schedule-import.js
   * `previewImport`). Reuses the existing legacy-cockpit route verbatim —
   * parsing and dedupe are unmodified (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001
   * §4.3, DEC-CHAT-20260930-204). Returns `{ status, ok, body }` where
   * `body` is `{ runId, format, preview }` on success or an error envelope
   * (`errorCode`/`message`) on refusal — never wrapped, and never a JSON
   * `Content-Type` header on the request (the browser sets the multipart
   * boundary itself; setting it manually breaks the server's own parse).
   * Same never-throw, never-hang contract as every other method: a thrown
   * or hung `getToken`/`fetchImpl` resolves `{ ok:false, status:0, body:
   * { errorCode } }`, never rejects.
   */
  async function previewScheduleImport(code, file, opts = {}) {
    const auth = await resolveAuthHeaders();
    if (auth.errorCode) return { status: 0, ok: false, body: { errorCode: auth.errorCode } };
    const form = new FormData();
    form.append("file", file, file && file.name);
    form.append("format", opts.format || "auto");
    const fetched = await fetchWithTimeout(`/api/projects/${encodeURIComponent(code)}/schedule/imports`, {
      method: "POST",
      headers: auth.headers,
      body: form,
    });
    if (fetched.error) return { status: 0, ok: false, body: { errorCode: fetched.error } };
    const res = fetched.value;
    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return { status: res.status, ok: res.ok, body };
  }

  /**
   * POST /api/projects/{code}/schedule/imports/{runId}/commit — one commit
   * pass (azure-functions/cis/CisScheduleImportCommit, shared/schedule-
   * import.js `commitImport`), reused verbatim. A large import answers 200
   * `committed_partial` with writes still outstanding (`body.counts.
   * remaining`) — the caller is meant to call this again until `body.status
   * === "committed"` with zero remaining and zero `body.counts.error`, same
   * as the legacy cockpit's loop (`cis-capture/app/js/cockpit.js:977-1020`).
   * This method makes exactly ONE call per invocation; the retry loop lives
   * in the caller so each pass's result is observable. `body.selectedKeys`
   * (owner row-level selection, 6 Oct 2026 feedback) is additive and
   * optional — omitted (or `body` omitted entirely) keeps the full-diff
   * commit this method always made.
   */
  async function commitScheduleImport(code, runId, body) {
    // A pass is budgeted server-side to finish in about 15 s; 45 s is the
    // per-call bound so a slow pass is not abandoned while it is still
    // working. The retry-once-then-continue policy lives in importCommit.js.
    return request(
      "POST",
      `/api/projects/${encodeURIComponent(code)}/schedule/imports/${encodeURIComponent(runId)}/commit`,
      body === undefined ? {} : body,
      commitTimeoutMs
    );
  }

  /** GET /api/projects/{code}/ncrs — returns the RAW row array (azure-functions/cis/CisNcrsQuery). */
  async function fetchNcrs(code) {
    return getJson(`/api/projects/${encodeURIComponent(code)}/ncrs`);
  }

  /**
   * GET /api/projects/{code}/inspections?from=&to= — register rows
   * (azure-functions/cis/CisInspectionsQuery). Plan's detail panel (codex
   * review gap (a)) uses this to resolve a tile/backlog row's
   * `clientCaptureId` from its `inspectionNumber` — neither the Plan
   * envelope nor CisCockpitPlan carries that field for any row. `from`/`to`
   * omitted reads the whole register, same as the route's own default.
   */
  async function fetchInspectionsRange(code, from, to) {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();
    return getJson(`/api/projects/${encodeURIComponent(code)}/inspections${qs ? `?${qs}` : ""}`);
  }

  /**
   * GET /api/projects/{code}/inspections/{captureId}/photos — photo
   * metadata for one capture (azure-functions/cis/CisPhotosList). Returns
   * the RAW row array (`[{ clientPhotoId, caption, capturedAtUtc,
   * contentHash, driveItemId }]`).
   */
  async function fetchCapturePhotos(code, captureId) {
    return getJson(`/api/projects/${encodeURIComponent(code)}/inspections/${encodeURIComponent(captureId)}/photos`);
  }

  /**
   * GET /api/projects/{code}/photos/{photoId}/thumbnail — one photo's bytes
   * (azure-functions/cis/CisPhotoThumbnail), as a Blob — never JSON-parsed,
   * a binary stream. `driveItemId` (optional, from the same photo's own
   * `fetchCapturePhotos` row) lets the server skip its own lookup; omitted
   * is still correct, just one extra Graph round trip server-side. Same
   * never-throw, never-hang contract as every other method.
   */
  async function fetchPhotoThumbnailBlob(code, photoId, driveItemId) {
    return withTransientRetry(() => fetchPhotoThumbnailBlobOnce(code, photoId, driveItemId), { delaysMs: retryDelaysMs, sleep });
  }

  async function fetchPhotoThumbnailBlobOnce(code, photoId, driveItemId) {
    return fetchPhotoBlobOnce("thumbnail", code, photoId, driveItemId);
  }

  async function fetchPhotoBlobOnce(kind, code, photoId, driveItemId) {
    const auth = await resolveAuthHeaders();
    if (auth.errorCode) return { status: 0, ok: false, blob: null };
    const qs = driveItemId ? `?item=${encodeURIComponent(driveItemId)}` : "";
    const fetched = await fetchWithTimeout(
      `/api/projects/${encodeURIComponent(code)}/photos/${encodeURIComponent(photoId)}/${kind}${qs}`,
      { method: "GET", headers: auth.headers }
    );
    if (fetched.error) return { status: 0, ok: false, blob: null, body: { errorCode: fetched.error } };
    const res = fetched.value;
    let blob = null;
    try {
      blob = await res.blob();
    } catch {
      blob = null;
    }
    return { status: res.status, ok: res.ok, blob };
  }

  /**
   * GET /api/projects/{code}/inspections/{captureId}/amendments — the change
   * history of one capture, oldest first (azure-functions/cis/CisAmendmentsList):
   * `[{ seq, fields, reason, void, amendedBy, amendedAtUtc, source }]`.
   */
  async function fetchCaptureAmendments(code, captureId) {
    return getJson(`/api/projects/${encodeURIComponent(code)}/inspections/${encodeURIComponent(captureId)}/amendments`);
  }

  /**
   * GET /api/projects/{code}/photos/{photoId}/content — one photo at full
   * size (azure-functions/cis/CisPhotoContent), as a Blob. Same contract as
   * the thumbnail read: never JSON-parsed, never throws, retried when transient.
   */
  async function fetchPhotoContentBlob(code, photoId, driveItemId) {
    return withTransientRetry(() => fetchPhotoBlobOnce("content", code, photoId, driveItemId), { delaysMs: retryDelaysMs, sleep });
  }

  /** PATCH /api/projects/{code}/ncrs/{ncrNumber} — returns the row read-back or an error envelope (azure-functions/cis/CisNcrsPatch). */
  async function patchNcr(code, ncrNumber, body) {
    return patchJson(`/api/projects/${encodeURIComponent(code)}/ncrs/${encodeURIComponent(ncrNumber)}`, body);
  }

  /**
   * GET /api/projects/{code}/ncrs/{ncrNumber}/returns — this NCR's return
   * history, oldest first (azure-functions/cis/CisNcrsReturns,
   * SPEC-CIS-NCR-RETURN-RESUBMIT-001 §5). Returns the RAW row array.
   */
  async function fetchNcrReturns(code, ncrNumber) {
    return getJson(`/api/projects/${encodeURIComponent(code)}/ncrs/${encodeURIComponent(ncrNumber)}/returns`);
  }

  /**
   * POST /api/projects/{code}/ncrs/exports — the yard-transmission register
   * package (azure-functions/cis/CisNcrExport, SM-only server-side): renders
   * the Owner's-Comments-format register (.xlsx), bundles the referenced
   * photos and archives both to SharePoint. An empty body `{}` is the
   * endpoint's own default filter — Open + Answered (shared/ncr-export.js
   * `validateExportFilter`), exactly what the capture app sent before this
   * action moved here (EP-CIS-004, DEC-CHAT-20261010-404). Body on 200 is
   * `{ runId, fileUrl, itemCount, photoCount, gaps }`.
   */
  async function exportNcrs(code) {
    return postJson(`/api/projects/${encodeURIComponent(code)}/ncrs/exports`, {});
  }

  /** POST /api/cockpit/ncrs/raise — returns the batch envelope (azure-functions/cis/CisCockpitNcrRaise, §4.5). */
  async function raiseNcrs(body) {
    return postJson("/api/cockpit/ncrs/raise", body);
  }

  /**
   * POST /api/cockpit/ncrs/raise?preview=1 — a pure read (CisCockpitNcrRaise
   * index.js:37 preview branch, shared/raise.js previewProjectRaise): same
   * request body as raise, returns the exact recipients/subject/body that
   * WOULD be sent, per project, without sending or writing anything. The UI
   * renders this verbatim — never an approximation.
   */
  async function previewRaise(body) {
    return postJson("/api/cockpit/ncrs/raise?preview=1", body);
  }

  /** POST /api/cockpit/ncrs/raise/{runId}/resolve — returns `{ runId, status }` or an error envelope (§4.5). */
  async function resolveNcrRaise(runId, body) {
    return postJson(`/api/cockpit/ncrs/raise/${encodeURIComponent(runId)}/resolve`, body);
  }

  /**
   * POST /api/projects/{code}/reports/weekly — the data-complete gate and
   * generation trigger (§4.6, azure-functions/cis/CisReportWeekly). Returns
   * the sync pipeline's JSON result (or the async 202 job envelope, never
   * requested by this screen — the sync path is what the UI drives) or an
   * error envelope (422 `data_incomplete`, 409 `generation_in_progress`).
   */
  async function postReportWeekly(code, body) {
    return postJson(`/api/projects/${encodeURIComponent(code)}/reports/weekly`, body);
  }

  /**
   * GET /api/projects/{code}/reports/weekly/status/{jobId} — where a queued
   * generation has got to (azure-functions/cis/CisReportStatus): 202
   * `{state:"running"}` until the worker finishes, then 200 with the job
   * record (`state` ready | no_prose | refused_no_data | failed, `errorCode`).
   */
  async function fetchReportStatus(code, jobId) {
    return getJson(`/api/projects/${encodeURIComponent(code)}/reports/weekly/status/${encodeURIComponent(jobId)}`);
  }

  /**
   * PATCH /api/projects/{code}/reports/weekly/{reportNumber} — approve |
   * reopen | publish | render_pdf (§4.6, SPEC-CIS-REPORT-PDF-PUBLISH-001 §4.2, azure-functions/cis/CisReportWeeklyPatch).
   * Returns the row read-back or an error envelope (409 `stale_etag` /
   * `already_published` / `report_issued`, 422 `invalid_transition`, 404
   * `publish_target_missing`, 500 `readback_mismatch`, 502 `pdf_conversion_failed`).
   * Approve and render_pdf answer the row after the PDF stamp (its new `etag`)
   * plus `pdf: { status: "ready" | "failed", url? }`.
   */
  async function patchReportWeekly(code, reportNumber, body) {
    return patchJson(`/api/projects/${encodeURIComponent(code)}/reports/weekly/${encodeURIComponent(reportNumber)}`, body);
  }

  return {
    fetchToday,
    reviewRecords,
    fetchScheduleRows,
    fetchReports,
    fetchCaptureAmendments,
    fetchPhotoContentBlob,
    remind,
    remindPhoto,
    waivePhoto,
    fetchReportVersions,
    fetchImportHistory,
    fetchPlan,
    upsertSchedule,
    previewScheduleImport,
    commitScheduleImport,
    fetchNcrs,
    fetchInspectionsRange,
    fetchCapturePhotos,
    fetchPhotoThumbnailBlob,
    patchNcr,
    fetchNcrReturns,
    exportNcrs,
    raiseNcrs,
    resolveNcrRaise,
    previewRaise,
    postReportWeekly,
    fetchReportStatus,
    patchReportWeekly,
  };
}
